import { Link } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";

const sectionLinks = [
  { label: "Problem", href: "#problem" },
  { label: "Solution", href: "#solution" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Demo", href: "#demo" },
  { label: "Features", href: "#features" },
  { label: "Use Cases", href: "#use-cases" },
];

const legalLinks = [
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms of Use", to: "/terms" },
  { label: "Advertising and Cookies", to: "/cookies" },
];

export default function Footer() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer data-testid="footer" className="relative hero-gradient-bg overflow-hidden">
      <div className="absolute inset-0 hero-animated-gradient" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr] gap-12 lg:gap-20">
          {/* Column 1 - Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-stripe-primary to-cyan-400 flex items-center justify-center shadow-md shadow-stripe-primary/20">
                <span className="text-white font-heading font-bold text-sm">S</span>
              </div>
              <span className="font-heading font-semibold text-lg text-navy">SkillsimAI</span>
            </div>
            <p className="text-sm text-stripe-text-secondary leading-relaxed">
              AI-powered soft skills training that transforms how teams communicate, negotiate, and lead through realistic practice and feedback.
            </p>
          </div>

          {/* Column 2 - Section Links (2 sub-columns) */}
          <div>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
              {sectionLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => scrollTo(link.href)}
                    className="text-sm text-stripe-text-secondary hover:text-navy transition-colors duration-200"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Contact */}
          <div>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-stripe-text-secondary">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-stripe-primary" />
                <span>1 Canada Square, Canary Wharf,<br />London E14 5AB, UK</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-stripe-text-secondary">
                <Phone className="w-4 h-4 text-cyan-500" />
                <a href="tel:+442030593450" className="hover:text-navy transition-colors duration-200">
                  +44 20 3059 3450
                </a>
              </li>
              <li className="flex items-center gap-2 text-sm text-stripe-text-secondary">
                <Mail className="w-4 h-4 text-emerald-500" />
                <a href="mailto:info@skillsimai.com" className="hover:text-navy transition-colors duration-200">
                  info@skillsimai.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-stripe-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-stripe-text-muted">
            &copy; {new Date().getFullYear()} SkillsimAI. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {legalLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="text-xs text-stripe-text-muted hover:text-stripe-text-secondary transition-colors duration-200"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
